import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  Select,
  MenuItem,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material'
import api from '../../../services/api'

/**
 * Education track type configuration component
 * Rendered within the Agent Config section for education tracks
 */
function EducationConfig({ config = {}, onChange, trackCode }) {
  const [modules, setModules] = useState([])
  const [loadingModules, setLoadingModules] = useState(false)
  const [modulesError, setModulesError] = useState(null)

  useEffect(() => {
    const fetchModules = async () => {
      setLoadingModules(true)
      setModulesError(null)
      try {
        const response = await api.get('/education/modules?status=published')
        setModules(response.data || [])
      } catch (error) {
        console.error('Failed to fetch education modules:', error)
        setModulesError('Failed to load education modules')
        setModules([])
      } finally {
        setLoadingModules(false)
      }
    }
    fetchModules()
  }, [])

  const updateConfig = (field, value) => {
    onChange({ ...config, [field]: value })
  }

  const updateNestedConfig = (parent, field, value) => {
    onChange({
      ...config,
      [parent]: { ...(config[parent] || {}), [field]: value },
    })
  }

  const handleModuleSelect = (moduleId) => {
    const selectedModule = modules.find((m) => m.module_id === moduleId)
    if (selectedModule) {
      onChange({
        ...config,
        module_id: moduleId,
        knowledge_base_id: selectedModule.knowledge_base_id || config.knowledge_base_id,
        chapter_count: selectedModule.chapter_count || config.chapter_count,
      })
    } else {
      updateConfig('module_id', moduleId)
    }
  }

  const selectedModule = modules.find((m) => m.module_id === config.module_id)

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 0.5 }}>
        Education Configuration
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Content delivery and progression settings for this educational track.
      </Typography>

      {modulesError && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
          {modulesError}
        </Alert>
      )}

      {/* Module & Content Settings */}
      <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center">
        <Typography variant="body2" color="text.secondary">Module</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            size="small"
            value={config.module_id || ''}
            onChange={(e) => handleModuleSelect(e.target.value)}
            disabled={loadingModules}
            displayEmpty
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">
              <em>None (manual)</em>
            </MenuItem>
            {modules.map((module) => (
              <MenuItem key={module.module_id} value={module.module_id}>
                <Box>
                  <Typography variant="body2" fontSize="0.8125rem">{module.title}</Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.6875rem">
                    {module.module_id} — {module.chapter_count || '?'} chapters — v{module.version}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {loadingModules && <CircularProgress size={16} />}
          {selectedModule && (
            <>
              <Chip label={`${selectedModule.chapter_count || 0} ch`} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />
              <Chip label={`${selectedModule.lesson_count || 0} lessons`} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />
              <Chip label={`v${selectedModule.version}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
            </>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">Knowledge Base</Typography>
        <TextField
          size="small"
          value={config.knowledge_base_id || ''}
          onChange={(e) => updateConfig('knowledge_base_id', e.target.value)}
          placeholder="Dify knowledge base ID"
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
          sx={{ maxWidth: 360 }}
        />

        <Typography variant="body2" color="text.secondary">Chapters</Typography>
        <TextField
          size="small"
          type="number"
          value={config.chapter_count ?? ''}
          onChange={(e) => updateConfig('chapter_count', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          inputProps={{ min: 1 }}
          sx={{ width: 100 }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Progression Options */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1 }}>
        Progression
      </Typography>
      <Box display="grid" gridTemplateColumns="120px 1fr" gap={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">Skip chapters</Typography>
        <Switch
          size="small"
          checked={config.allow_skip_chapters === true}
          onChange={(e) => updateConfig('allow_skip_chapters', e.target.checked)}
        />

        <Typography variant="body2" color="text.secondary">Skip lessons</Typography>
        <Switch
          size="small"
          checked={config.allow_skip_lessons !== false}
          onChange={(e) => updateConfig('allow_skip_lessons', e.target.checked)}
        />

        <Typography variant="body2" color="text.secondary">Comprehension</Typography>
        <Switch
          size="small"
          checked={config.comprehension_check !== false}
          onChange={(e) => updateConfig('comprehension_check', e.target.checked)}
        />

        {config.comprehension_check !== false && (
          <>
            <Typography variant="body2" color="text.secondary">Passing score</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 300 }}>
              <Slider
                size="small"
                value={config.comprehension_threshold || 0.7}
                onChange={(e, value) => updateConfig('comprehension_threshold', value)}
                min={0.5}
                max={1}
                step={0.05}
                marks={[
                  { value: 0.5, label: '50%' },
                  { value: 0.7, label: '70%' },
                  { value: 1, label: '100%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              />
              <Typography variant="body2" sx={{ minWidth: 36, textAlign: 'right' }}>
                {Math.round((config.comprehension_threshold || 0.7) * 100)}%
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Quiz Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1 }}>
        Quiz
      </Typography>
      <Box display="grid" gridTemplateColumns="120px 1fr" gap={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">Questions/ch</Typography>
        <TextField
          size="small"
          type="number"
          value={config.quiz_settings?.questions_per_chapter ?? 3}
          onChange={(e) => updateNestedConfig('quiz_settings', 'questions_per_chapter', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
          inputProps={{ min: 1, max: 10 }}
          sx={{ width: 100 }}
        />

        <Typography variant="body2" color="text.secondary">Allow retry</Typography>
        <Switch
          size="small"
          checked={config.quiz_settings?.allow_retry !== false}
          onChange={(e) => updateNestedConfig('quiz_settings', 'allow_retry', e.target.checked)}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Exercise Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem', mb: 1 }}>
        Exercises
      </Typography>
      <Box display="grid" gridTemplateColumns="120px 1fr" gap={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">Reminders</Typography>
        <Switch
          size="small"
          checked={config.exercise_settings?.enable_reminders !== false}
          onChange={(e) => updateNestedConfig('exercise_settings', 'enable_reminders', e.target.checked)}
        />

        <Typography variant="body2" color="text.secondary">Confirm done</Typography>
        <Switch
          size="small"
          checked={config.exercise_settings?.require_completion_confirmation !== false}
          onChange={(e) => updateNestedConfig('exercise_settings', 'require_completion_confirmation', e.target.checked)}
        />
      </Box>
    </Box>
  )
}

export default EducationConfig
